import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { GLOBE_RADIUS, latLonToVector3, VOLCANOES } from "../lib/seismic";
import { useSim } from "../lib/store";

const UP = new THREE.Vector3(0, 1, 0);

// Soft radial dot sprite — same style used by PopulationClusters / tsunami points.
function softDot() {
  const c = document.createElement("canvas");
  c.width = c.height = 64;
  const ctx = c.getContext("2d");
  const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  g.addColorStop(0, "rgba(255,255,255,1)");
  g.addColorStop(0.3, "rgba(255,255,255,0.7)");
  g.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 64, 64);
  return new THREE.CanvasTexture(c);
}

// 100 volcano markers as a glowing orange additive point-cloud (soft-dot sprite),
// matching PopulationClusters. Active eruptions keep a pulsing glow ring + light.
export function VolcanoNodes() {
  const matRef = useRef();
  const events = useSim((s) => s.events);
  const tex = useMemo(() => softDot(), []);

  const placements = useMemo(
    () =>
      VOLCANOES.map((v) => {
        const pos = latLonToVector3(v.lat, v.lon, GLOBE_RADIUS + 0.014);
        const normal = pos.clone().normalize();
        const q = new THREE.Quaternion().setFromUnitVectors(UP, normal);
        return { v, pos, q };
      }),
    []
  );

  const geom = useMemo(() => {
    const pos = new Float32Array(placements.length * 3);
    placements.forEach((p, i) => {
      pos[i * 3] = p.pos.x;
      pos[i * 3 + 1] = p.pos.y;
      pos[i * 3 + 2] = p.pos.z;
    });
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    return g;
  }, [placements]);

  const activeNames = useMemo(() => {
    const set = new Set();
    for (const e of events) if (e.activeEruption) set.add(e.activeEruption.name);
    return set;
  }, [events]);

  const activeGlows = useMemo(
    () => placements.filter((p) => activeNames.has(p.v.name)),
    [placements, activeNames]
  );

  const glowRefs = useRef([]);
  useFrame((state) => {
    const t = state.clock.elapsedTime;
    const intro = useSim.getState().introPlaying ? 0.35 : 0;
    if (matRef.current) {
      matRef.current.opacity = 0.7 + Math.sin(t * 1.8) * 0.2 + intro;
      matRef.current.size = 0.075 + Math.sin(t * 2.4) * 0.01;
    }
    glowRefs.current.forEach((g) => {
      if (!g) return;
      g.scale.setScalar(1.4 + Math.sin(t * 3) * 0.5);
      g.material.opacity = 0.75 + intro;
    });
  });

  return (
    <group>
      <points geometry={geom} frustumCulled={false}>
        <pointsMaterial
          ref={matRef}
          map={tex}
          color="#ff7a1a"
          size={0.075}
          sizeAttenuation
          transparent
          opacity={0.7}
          depthWrite={false}
          blending={THREE.AdditiveBlending}
          toneMapped={false}
        />
      </points>
      {activeGlows.map((p, i) => (
        <group key={p.v.name} position={p.pos} quaternion={p.q}>
          <mesh ref={(el) => (glowRefs.current[i] = el)} position={[0, 0.01, 0]} rotation={[-Math.PI / 2, 0, 0]}>
            <ringGeometry args={[0.022, 0.036, 24]} />
            <meshBasicMaterial color="#ff8c1a" transparent opacity={0.8} side={THREE.DoubleSide} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
          </mesh>
          <pointLight color="#ff5a00" intensity={2.5} distance={1.4} decay={2} position={[0, 0.05, 0]} />
        </group>
      ))}
    </group>
  );
}
